/* global google */
/*
 * Mapeo canónico `PlaceResult` / `GeocoderResult` → `Commerce['address']` estructurado.
 * Mismo criterio de `address_components` que usa el AddressForm del panel; centralizado para que el
 * portal partner (alta/configuración) y cualquier otro consumidor del mapa compartan la fuente única.
 *
 * Acepta tanto el resultado del Autocomplete (elegir una dirección) como el del Geocoder (reverse-
 * geocode al arrastrar el marcador): ambos exponen `address_components`, `formatted_address` y
 * `geometry.location`, así el mismo mapeo llena los campos en los dos flujos.
 */
import type { Commerce } from '@interfaces/general';

type CommerceAddress = NonNullable<Commerce['address']>;

// Forma mínima común a `google.maps.places.PlaceResult` y `google.maps.GeocoderResult`.
type GeocodableResult = google.maps.places.PlaceResult | google.maps.GeocoderResult;

export const mapPlaceToAddress = (place: GeocodableResult): CommerceAddress => {
  const components = place.address_components || [];
  // Primer componente cuyo `types` incluya alguno de los tipos pedidos (en orden de preferencia).
  const pick = (...types: string[]): string => {
    for (const type of types) {
      const match = components.find((component) => component.types.includes(type));
      if (match?.long_name) return match.long_name;
    }
    return '';
  };
  const loc = place.geometry?.location;

  return {
    country: pick('country') || 'Mexico',
    state: pick('administrative_area_level_1'),
    // En MX la ciudad suele venir como `locality`; con fallbacks a sublocality / municipio para que
    // el campo Ciudad no quede vacío cuando Google no devuelve `locality`.
    city: pick('locality', 'sublocality', 'administrative_area_level_2', 'administrative_area_level_3'),
    // La colonia puede venir como `neighborhood` o como alguna variante de `sublocality`.
    neighborhood: pick('neighborhood', 'sublocality', 'sublocality_level_1', 'sublocality_level_2'),
    postalCode: pick('postal_code'),
    street: pick('route'),
    exteriorNumber: pick('street_number'),
    interiorNumber: '',
    fullAddress: place.formatted_address || '',
    latitude: loc ? loc.lat() : 0,
    longitude: loc ? loc.lng() : 0,
  };
};

export default mapPlaceToAddress;
